"use client";

import { Dialog } from "@headlessui/react";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";

import { HTMLMarkdown } from "@/components/markdown/HTMLMarkdown";
import { closeVIPDialog } from "@/context/redux/reducers/dialog/vip-dialog-slice";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import {
  Plan,
  PlanInfo,
  TiersData,
  tiersSchema,
} from "@/features/stock-mining-pricing/types/pricing-tiers";
import { getPlanPeriod } from "@/features/stripe/utils/stripe-plan";
import { useAppDispatch, useAppSelector } from "@/hooks/redux-hooks";
import { useLang } from "@/hooks/use-lang";
import {
  getPricingDataQueryKeyMap,
  usePublicData,
} from "@/services/public-data";

import { Button } from "ui/core/button";

// this is a dialog that shows up when a user is a VIP member.
// vip members will upgrade to a premium plan after we announce the premium plan, but in stripe platform, user still see they are basic plan, so we need to show this dialog to let the user know that they are already a VIP member and they will be upgraded to the premium plan after we announce the premium plan
export const VIPDialog = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { isOpen, redirectUrl } = useAppSelector((state) => state.vipDialog);
  const { t, lang } = useLang();
  const { profile } = useAuth();
  const publicDataItemName = getPricingDataQueryKeyMap(lang).pricingTiers;

  const { data } = usePublicData<TiersData>({
    itemName: publicDataItemName,
    schema: tiersSchema,
    enabled: isOpen,
  });

  const plan = profile?.plan ?? "Free";
  const period = getPlanPeriod(plan);
  const stripePlanData = data?.[period] ?? [];

  const getPlanDetails = (data: PlanInfo[], plan: Plan) => {
    const planData = data.find((item) => plan === item.plan);
    return {
      name: planData?.name ?? "",
      price: planData?.pricing?.fee ?? 0,
    };
  };

  const { name: premiumPlanName, price: premiumPrice } = getPlanDetails(
    stripePlanData,
    "premium"
  );
  const { name: basicPlanName, price: basicPrice } = getPlanDetails(
    stripePlanData,
    "basic"
  );

  return (
    <Dialog open={isOpen} onClose={() => {}}>
      <div
        id="backdrop"
        className="fixed inset-0 z-[99999] bg-black/30"
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-[99999] flex w-screen items-center justify-center p-4">
        <Dialog.Panel className="mx-auto flex max-w-3xl flex-col gap-5 rounded-2xl bg-white p-8">
          <Dialog.Title className="text-base font-bold">
            {t.pricing.vipTitle}
          </Dialog.Title>
          <Dialog.Description className="text-sm text-zinc-500">
            <HTMLMarkdown>
              {t.pricing.vipContent({
                basicPlanName,
                basicPrice,
                premiumPlanName,
                premiumPrice,
              })}
            </HTMLMarkdown>
          </Dialog.Description>
          <Button
            className="mx-auto w-full max-w-xs"
            onClick={() => {
              if (redirectUrl) {
                router.push(redirectUrl);
              } else {
                toast.error(t.error.serverError);
                dispatch(closeVIPDialog());
              }
            }}
          >
            {t.pricing.ok}
          </Button>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
